<p-dialog
  [visible]="localVisible()"
  (visibleChange)="onVisibilityChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [closable]="true"
  [closeOnEscape]="true"
  [style]="{ width: '58vw', height: '100vh' }"
  [styleClass]="'pdf-viewer-dialog'"
  [maskStyleClass]="'pdf-viewer-dialog-mask'"
  [position]="'right'"
  [header]="data()?.title || data()?.filename || 'Document Viewer'"
  [baseZIndex]="10000"
  (onHide)="onClose()"
  (onShow)="onShow()">

  <div class="pdf-viewer-content">
    @if (loading()) {
      <div class="flex justify-content-center align-items-center h-full">
        <p-progressspinner></p-progressspinner>
      </div>
    }

    @if (error()) {
      <div class="flex flex-column justify-content-center align-items-center h-full text-center">
        <i class="pi pi-exclamation-triangle text-6xl text-orange-500 mb-3"></i>
        <h3>Unable to load document</h3>
        <p class="text-color-secondary">{{ error() }}</p>
        <p-button
          label="Retry"
          icon="pi pi-refresh"
          (onClick)="loadDocument()"
          [loading]="loading()"
          severity="secondary"
          size="small" />
      </div>
    }

    @if (pdfSrc() && !loading() && !error()) {
      <div class="pdf-viewer-container" [class.sidebar-collapsed]="!showSidebar()">
        <!-- Initialization Overlay -->
        @if (initializing()) {
          <div class="initialization-overlay">
            <div class="initialization-content">
              <p-progressspinner [style]="{'width': '50px', 'height': '50px'}"></p-progressspinner>
            </div>
          </div>
        }

        <!-- Custom Toolbar -->
        <p-toolbar styleClass="pdf-toolbar">
          <div class="p-toolbar-group-start">
            <!-- Toggle Sidebar -->
            <p-button
              [icon]="showSidebar() ? 'pi pi-angle-left' : 'pi pi-angle-right'"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              (onClick)="toggleSidebar()"
              pTooltip="Toggle Thumbnails"
              styleClass="mr-2" />
            <div class="toolbar-divider"></div>
            <!-- Zoom Controls -->
            <p-button
              icon="pi pi-minus"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              (onClick)="zoomOut()"
              pTooltip="Zoom Out" />

            <span class="zoom-info mx-2">
              {{ Math.round(zoomLevel() * 100) }}%
            </span>

            <p-button
              icon="pi pi-plus"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              (onClick)="zoomIn()"
              pTooltip="Zoom In" />

            <p-button
              label="Fit Width"
              [text]="true"
              severity="secondary"
              (onClick)="fitToWidth()"
              styleClass="ml-2" />

            <p-button
              label="Fit Page"
              [text]="true"
              severity="secondary"
              (onClick)="fitToPage()"
              styleClass="ml-1" />
          </div>

          <div class="p-toolbar-group-center">
            <!-- Navigation Controls -->
            <div class="page-navigation">
              <p-button
                icon="pi pi-chevron-left"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                size="small"
                (onClick)="previousPage()"
                [disabled]="currentPage() <= 1"
                pTooltip="Previous Page" />

              <span class="page-info mx-2">
                <input
                  type="number"
                  [(ngModel)]="pageInput"
                  (ngModelChange)="goToPage()"
                  [min]="1"
                  [max]="totalPages()"
                  class="page-input"
                  pInputText />
                <span class="mx-1">of {{ totalPages() }}</span>
              </span>

              <p-button
                icon="pi pi-chevron-right"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                size="small"
                (onClick)="nextPage()"
                [disabled]="currentPage() >= totalPages()"
                pTooltip="Next Page" />
            </div>
          </div>

          <div class="p-toolbar-group-end">
            <!-- Search and Download -->
            <div class="search-container">
              <i class="pi pi-search search-icon"></i>
              <input
                type="text"
                pInputText
                placeholder="Search... (Ctrl+F)"
                [(ngModel)]="searchQuery"
                (ngModelChange)="onSearchChange()"
                class="search-input" />
              @if (searchResultsCount() > 0) {
                <span class="search-results-count">
                  {{ currentSearchMatch() }}/{{ searchResultsCount() }}
                </span>
              }
              <div class="search-nav-buttons">
                <button
                  type="button"
                  class="search-nav-btn"
                  (click)="findPrevious()"
                  [disabled]="!searchQuery || searchResultsCount() === 0"
                  pTooltip="Previous Match">
                  <i class="pi pi-chevron-up"></i>
                </button>
                <button
                  type="button"
                  class="search-nav-btn"
                  (click)="findNext()"
                  [disabled]="!searchQuery || searchResultsCount() === 0"
                  pTooltip="Next Match">
                  <i class="pi pi-chevron-down"></i>
                </button>
              </div>
            </div>

            <p-button
              icon="pi pi-download"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              (onClick)="downloadDocument()"
              pTooltip="Download"
              styleClass="ml-2" />
          </div>
        </p-toolbar>

        <!-- Main Content Area -->
        <div class="pdf-content-area">
          <!-- Thumbnail Sidebar -->
          @if (showSidebar()) {
            <div class="thumbnail-sidebar">
              @if (generatingThumbnails()) {
                <div class="thumbnail-loading">
                  <p-progressspinner styleClass="w-3rem h-3rem" strokeWidth="4"></p-progressspinner>
                  <span class="text-sm">Generating thumbnails...</span>
                </div>
              }
              <div class="thumbnail-list">
                @for (thumbnail of thumbnails(); track thumbnail.page) {
                  <div
                    class="thumbnail-item"
                    [class.active]="thumbnail.page === currentPage()"
                    (click)="goToPageFromThumbnail(thumbnail.page)"
                    [attr.title]="'Page ' + thumbnail.page">
                    @if (thumbnail.url) {
                      <img
                        [src]="thumbnail.url"
                        [alt]="'Page ' + thumbnail.page"
                        class="thumbnail-image" />
                    } @else {
                      <div class="thumbnail-placeholder">
                        <span class="page-number">{{ thumbnail.page }}</span>
                      </div>
                    }
                    <div class="thumbnail-label">{{ thumbnail.page }}</div>
                  </div>
                }
              </div>
            </div>
          }

          <!-- PDF Viewer -->
          <div class="pdf-canvas-container" #pdfContainer (scroll)="onScroll($event)">
          <pdf-viewer
            #pdfViewer
            [src]="pdfSrc()!"
            [(page)]="pageInput"
            [zoom]="zoomLevel()"
            [c-maps-url]="'/assets/pdfjs/cmaps/'"
            [show-all]="true"
            [render-text]="true"
            [stick-to-page]="false"
            (after-load-complete)="onPdfLoaded($event)"
            (page-rendered)="onPageRendered($event)"
            (error)="onPdfError($event)"
            (pageChange)="onPageChange($event)"
            style="display: block; width: 100%; height: 100%;">
          </pdf-viewer>
          </div>
        </div>
      </div>
    }
  </div>
</p-dialog>